import { registerIndicator, type KLineData, type Indicator } from 'klinecharts'
import { boolParam, numParam, strParam } from './calcParams'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import { binVolumeByPrice, collectProfileBars, type VolumeProfile } from './volumeProfile'
import {
  lastSessionDaysStart,
  sessionDayKey,
  sessionTimezoneOf,
  timestampsOf,
  toSessionAnchor,
  visibleSessionRuns,
  type SessionAnchor,
  type SessionRun,
} from './sessionDay'

const VPVR_INDICATOR_NAME = 'VPVR'
const VPSV_INDICATOR_NAME = 'VPSV'
const VPXD_INDICATOR_NAME = 'VPXD'

// Defaults mirror the charts-indicators VPVR catalog palette so the
// no-style-override state matches what the settings drawer advertises. The draw
// reads overrides positionally from `indicator.styles.lines` — see COLOR_ORDER.
// Softer, desaturated fills so the profile (painted behind the candles) reads
// as a quiet backdrop, not a competitor. Kept in sync with the VPVR catalog
// figure colors in charts-indicators `catalog.ts`.
const PROFILE_COLOR = '#6E7480' // neutral (single-color, non-split profile)
const UP_COLOR = '#4C8B82'
const DOWN_COLOR = '#B05C68'
const POC_COLOR = '#FFB74D'

// The registered indicator carries `figures: []` (volume magnitudes must not
// enter the price pane's shared y-axis range), so buildIndicatorStyles has no
// runtime figures to key off. The CATALOG instead declares four `line` figures
// — profile, up, down, poc — which buildIndicatorStyles emits into
// `styles.lines[]` in THIS order. The draw indexes that array by position.
const PROFILE_LINE = 0
const UP_LINE = 1
const DOWN_LINE = 2
const POC_LINE = 3

/** Default param values, in calcParams order
 *  (rows, widthPct, valueAreaPct, split, placement[, sessionAnchor]). */
const DEFAULT_ROWS = 24
const DEFAULT_WIDTH_PCT = 30
const DEFAULT_VALUE_AREA_PCT = 70
const DEFAULT_PLACEMENT = 'left'
// Session profiles pin to each day's START (open) edge by default — the classic
// SVP look, growing rightward into the session.
const DEFAULT_SESSION_PLACEMENT = 'left'
// Calendar day by default — the conventional SVP. `prior-after-hours` is opt-in
// because it redefines what a "day" is, and that should be a choice.
const DEFAULT_SESSION_ANCHOR: SessionAnchor = 'calendar-day'
// Two days is the day-2 small-cap default: the runner's day-1 range plus the
// session being traded, which is exactly the volume that sets the levels.
const DEFAULT_DAYS = 2

/**
 * Where the N-day window is measured FROM.
 *
 *  - `latest-data` — the newest bar in the dataset. The window never moves, so the
 *    profile is identical at every scroll position. Right for live trading, but
 *    scrolling back to review a past setup leaves today's profile drawn over
 *    historical bars it does not describe.
 *  - `visible` — the right edge of the viewport. Scroll a past day to the right of
 *    the screen and you get the profile AS OF that day. Identical to `latest-data`
 *    while you are at the live edge, so it costs nothing there.
 */
const DEFAULT_WINDOW_ANCHOR = 'latest-data'

// The profile sits BEHIND the candles (zLevel -1), so its fills must stay quiet
// or they compete with the bars. Value-area buckets read at a soft ~0.5 and the
// rest fade further — deliberately translucent, not solid.
/** Opacity for value-area buckets (the emphasised band). */
const VALUE_AREA_ALPHA = 0.5
/** Opacity for buckets outside the value area. */
const DIM_ALPHA = 0.22
/** Vertical gap between adjacent bucket bars, in px. */
const BAR_GAP = 1

type ProfileCtx = Pick<
  CanvasRenderingContext2D,
  'save' | 'restore' | 'fillRect' | 'beginPath' | 'moveTo' | 'lineTo' | 'stroke'
> & {
  fillStyle: string | CanvasGradient | CanvasPattern
  strokeStyle: string | CanvasGradient | CanvasPattern
  lineWidth: number
  globalAlpha: number
}

export interface VolumeProfileDrawParams {
  ctx: ProfileCtx
  kLineDataList: KLineData[]
  visibleRange: { from: number; to: number }
  bounding: { width: number; height: number }
  yAxis: { convertToPixel: (value: number) => number }
  indicator: {
    calcParams?: unknown[]
    styles?: { lines?: Array<{ color?: string; size?: number }> } | null
  }
}

function lineColor(
  lines: Array<{ color?: string }> | undefined,
  idx: number,
  fallback: string,
): string {
  return lines?.[idx]?.color ?? fallback
}

/** The four positional style overrides resolved against their defaults. */
function profileColors(lines: Array<{ color?: string }> | undefined) {
  return {
    profile: lineColor(lines, PROFILE_LINE, PROFILE_COLOR),
    up: lineColor(lines, UP_LINE, UP_COLOR),
    down: lineColor(lines, DOWN_LINE, DOWN_COLOR),
    poc: lineColor(lines, POC_LINE, POC_COLOR),
  }
}

/** Max bucket volume — the POC bucket, by construction of `pointOfControl`. */
function maxBucketTotal(profile: NonNullable<ReturnType<typeof binVolumeByPrice>>): number {
  return profile.buckets[profile.pocIdx]?.total ?? 0
}

// ─── Draw-plan memo ──────────────────────────────────────────────────────────
//
// All three profiles used to re-bin from scratch on every draw frame — a fresh
// collectProfileBars allocation plus a full binVolumeByPrice pass per pan/zoom
// repaint, even when nothing feeding the profile had changed. The TPO draw
// plan (tpoIndicator.ts) solved exactly this, and what follows is its pattern
// ported: a WeakMap keyed on the bar array klinecharts hands `draw` (stable
// across frames, one entry per chart, garbage-collected with its data), with
// entries keyed on everything that CAN change a profile — the range binned,
// the binning params, and the live bar's mutable fields. Pixel work (anchors,
// widths, clamps) stays per-frame in the draws; it is cheap and genuinely
// viewport-dependent.

interface ProfileEntry {
  key: string
  profile: VolumeProfile | null
}

interface VolumeProfileDrawMemo {
  /** One slot per single-profile draw. VPVR and VPXD can be live on the same
   *  chart at once, alternating draws every frame — a shared slot would flip
   *  keys each draw and never hit. */
  vpvr: ProfileEntry
  vpxd: ProfileEntry
  /** Per-session entries for VPSV, keyed by session date. */
  sessionsKey: string
  sessions: Map<string, ProfileEntry>
}

/** Sessions kept per chart before the map resets (the TPO cap). Panning is a
 *  forward/backward sweep, so a cleared map simply refills with the sessions
 *  in view. */
const SESSION_CACHE_LIMIT = 256

const drawMemo = new WeakMap<KLineData[], VolumeProfileDrawMemo>()

function memoFor(list: KLineData[]): VolumeProfileDrawMemo {
  let memo = drawMemo.get(list)
  if (!memo) {
    memo = {
      vpvr: { key: '', profile: null },
      vpxd: { key: '', profile: null },
      sessionsKey: '',
      sessions: new Map(),
    }
    drawMemo.set(list, memo)
  }
  return memo
}

/** The newest bar's mutable fields — the only data that changes without the
 *  array's length or timestamps changing (an in-place live tick). Copied from
 *  the TPO memo rather than shared: extracting it would touch tpoIndicator.ts
 *  for the sake of five lines. */
function liveBarKey(list: KLineData[]): string {
  const last = list[list.length - 1]
  if (!last) return 'none'
  return `${last.timestamp}|${last.high}|${last.low}|${last.close}|${last.volume ?? 0}`
}

export type RangeProfileSlot = 'vpvr' | 'vpxd'

export interface RangeProfileOptions {
  rows: number
  valueAreaPct: number
}

/**
 * Bin `[start, end)` into a volume profile, memoized per bar array.
 *
 * Shared by VPVR (range = the visible bars) and VPXD (range = the rolling-days
 * window) — the binning is identical, only where the range comes from differs.
 *
 * Cache invalidation, via the key: the range's indices AND its first bar's
 * timestamp (indices alone go stale when a history prepend shifts the bars
 * under them), the binning params, and — only when the range reaches the live
 * edge — the last bar's mutable fields, so an in-place tick rebuilds a profile
 * that contains it but never one scrolled fully into history. Split, width and
 * placement are presentation, not binning, so they stay out of the key.
 * Returns the SAME object on a warm hit — the contract the unit tests pin.
 */
export function computeRangeProfile(
  kLineDataList: KLineData[],
  start: number,
  end: number,
  opts: RangeProfileOptions,
  slot: RangeProfileSlot,
): VolumeProfile | null {
  const entry = memoFor(kLineDataList)[slot]
  let key = `${start}|${end}|${opts.rows}|${opts.valueAreaPct}|${kLineDataList[start]?.timestamp ?? 'none'}`
  if (end >= kLineDataList.length) key += `|${liveBarKey(kLineDataList)}`
  if (entry.key !== key) {
    entry.key = key
    entry.profile = binVolumeByPrice(collectProfileBars(kLineDataList, start, end), {
      rows: opts.rows,
      valueAreaPct: opts.valueAreaPct,
    })
  }
  return entry.profile
}

export interface SessionProfilesOptions {
  rows: number
  valueAreaPct: number
  tz: string
  anchor: SessionAnchor
}

export interface SessionProfileSpan {
  run: SessionRun
  profile: VolumeProfile
}

/**
 * One profile per (partly) visible trading session — the VPSV half of the
 * draw-plan port. Each visible session is grown to its FULL day (a profile is
 * a property of the whole session, not just the on-screen bars) and its bins
 * are memoized per session date.
 *
 * Cache invalidation, from broadest to narrowest:
 *  - a binning-param or day-grouping change flips `sessionsKey`, rebuilds all;
 *  - an append or prepend changes a session's timestamp/bar-count fingerprint;
 *  - an in-place live tick changes only the newest session's fingerprint.
 * Returns referentially-equal profiles on a warm hit, which is also what the
 * unit tests assert the caching by. Sessions that bin to nothing drawable are
 * cached as null so an empty day is not re-binned every frame.
 */
export function computeSessionProfiles(
  kLineDataList: KLineData[],
  visibleRange: { from: number; to: number },
  opts: SessionProfilesOptions,
): SessionProfileSpan[] {
  const { rows, valueAreaPct, tz, anchor } = opts
  const from = Math.max(0, visibleRange.from)
  const to = Math.min(kLineDataList.length, visibleRange.to)
  if (to <= from) return []

  const timestamps = timestampsOf(kLineDataList)
  const runs = visibleSessionRuns(timestamps, from, to, tz, anchor)

  const memo = memoFor(kLineDataList)
  const sessionsKey = `${rows}|${valueAreaPct}|${anchor}|${tz}`
  if (memo.sessionsKey !== sessionsKey) {
    memo.sessionsKey = sessionsKey
    memo.sessions.clear()
  }

  const lastIdx = kLineDataList.length - 1
  const spans: SessionProfileSpan[] = []
  for (const run of runs) {
    const sessionDate = sessionDayKey(timestamps[run.startIdx], tz, anchor)
    const barCount = run.endIdx - run.startIdx + 1
    let entryKey = `${timestamps[run.startIdx]}|${timestamps[run.endIdx]}|${barCount}`
    if (run.endIdx === lastIdx) entryKey += `|${liveBarKey(kLineDataList)}`

    let entry = memo.sessions.get(sessionDate)
    if (!entry || entry.key !== entryKey) {
      const bars = collectProfileBars(kLineDataList, run.startIdx, run.endIdx + 1)
      entry = { key: entryKey, profile: binVolumeByPrice(bars, { rows, valueAreaPct }) }
      if (memo.sessions.size >= SESSION_CACHE_LIMIT) memo.sessions.clear()
      memo.sessions.set(sessionDate, entry)
    }
    if (entry.profile) spans.push({ run, profile: entry.profile })
  }
  return spans
}

/**
 * Paint a right-anchored volume-at-price histogram over the visible bars.
 *
 * Exported (pivot's drawPivotSegments precedent) so a fake-ctx harness can
 * assert bar rects, POC line, value-area alpha and split segments without the
 * chart bootstrap. Returns true (klinecharts skips its default render) in all
 * cases — including a null profile, where nothing is painted.
 */
export function drawVolumeProfile(params: VolumeProfileDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, bounding, yAxis, indicator } = params
  const cp = indicator.calcParams ?? []
  const rows = numParam(cp, 0, DEFAULT_ROWS)
  const widthPct = numParam(cp, 1, DEFAULT_WIDTH_PCT)
  const valueAreaPct = numParam(cp, 2, DEFAULT_VALUE_AREA_PCT)
  const split = boolParam(cp, 3, true)
  const placeLeft = strParam(cp, 4, DEFAULT_PLACEMENT) === 'left'

  const from = Math.max(0, visibleRange.from)
  const to = Math.min(kLineDataList.length, visibleRange.to)
  // Memoized: during a pan, from/to only move when a bar actually crosses the
  // viewport edge, so the sub-bar scroll frames in between reuse the profile.
  const profile = computeRangeProfile(kLineDataList, from, to, { rows, valueAreaPct }, 'vpvr')
  if (!profile || !(maxBucketTotal(profile) > 0)) return true

  ctx.save()
  paintProfile(ctx, yAxis, {
    profile,
    anchorX: placeLeft ? 0 : bounding.width,
    maxWidth: bounding.width * (widthPct / 100),
    placeLeft,
    pocX0: 0,
    pocX1: bounding.width,
    split,
    colors: profileColors(indicator.styles?.lines),
  })
  ctx.restore()

  return true
}

/** One profile's paint parameters (VPVR spans the pane; session spans a day). */
interface ProfilePaint {
  profile: NonNullable<ReturnType<typeof binVolumeByPrice>>
  /** Edge the bars hug: the right edge (grow left) or left edge (grow right). */
  anchorX: number
  /** Widest bar, in px. */
  maxWidth: number
  placeLeft: boolean
  /** POC level-line x-span (full pane for VPVR, the day's span for session). */
  pocX0: number
  pocX1: number
  split: boolean
  colors: { profile: string; up: string; down: string; poc: string }
}

/**
 * Paint one binned profile: horizontal bucket bars anchored to `anchorX`, a
 * dimmed remainder outside the value area, and the POC level line. Shared by
 * the visible-range (VPVR) and per-session draws.
 */
function paintProfile(
  ctx: ProfileCtx,
  yAxis: { convertToPixel: (value: number) => number },
  p: ProfilePaint,
): void {
  // A bar segment `off` px inward from the anchored edge, `w` px wide.
  const segX = (off: number, w: number) => (p.placeLeft ? p.anchorX + off : p.anchorX - off - w)
  const maxTotal = maxBucketTotal(p.profile)

  p.profile.buckets.forEach((bucket, i) => {
    const yTop = yAxis.convertToPixel(bucket.priceHigh)
    const yBottom = yAxis.convertToPixel(bucket.priceLow)
    const barH = Math.max(yBottom - yTop - BAR_GAP, 1)
    const totalW = maxTotal > 0 ? (bucket.total / maxTotal) * p.maxWidth : 0
    const inValueArea = i >= p.profile.valIdx && i <= p.profile.vahIdx
    ctx.globalAlpha = inValueArea ? VALUE_AREA_ALPHA : DIM_ALPHA

    if (p.split) {
      // up hugs the anchored edge; down continues inward from up's inner edge.
      const upW = bucket.total > 0 ? (bucket.up / bucket.total) * totalW : 0
      const downW = bucket.total > 0 ? (bucket.down / bucket.total) * totalW : 0
      if (upW > 0) {
        ctx.fillStyle = p.colors.up
        ctx.fillRect(segX(0, upW), yTop, upW, barH)
      }
      if (downW > 0) {
        ctx.fillStyle = p.colors.down
        ctx.fillRect(segX(upW, downW), yTop, downW, barH)
      }
    } else {
      ctx.fillStyle = p.colors.profile
      ctx.fillRect(segX(0, totalW), yTop, totalW, barH)
    }
  })

  // POC — a 1px level line at the point-of-control bucket's mid price.
  const poc = p.profile.buckets[p.profile.pocIdx]
  if (poc) {
    const yMid = yAxis.convertToPixel((poc.priceLow + poc.priceHigh) / 2)
    ctx.globalAlpha = 1
    ctx.strokeStyle = p.colors.poc
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(p.pocX0, yMid)
    ctx.lineTo(p.pocX1, yMid)
    ctx.stroke()
  }
}

// ─── Session Volume Profile (VPSV) ───────────────────────────────────────────

export interface SessionVolumeProfileDrawParams extends VolumeProfileDrawParams {
  /** Bar-index → x-pixel (bar center) and the per-bar width, for pinning each
   *  session's profile to its own column span. */
  xAxis: { convertToPixel: (dataIndex: number) => number }
  barSpace: { halfBar: number }
  indicator: VolumeProfileDrawParams['indicator'] & { extendData?: unknown }
}

/**
 * Session Volume Profile: one independent profile per TRADING DAY, each pinned
 * inside that day's column span. Unlike VPVR (one profile over the whole
 * visible range), the bars group by exchange-session day (ET by default; a
 * `{timezone}` in the indicator's extendData overrides), and each day's profile
 * scales to its own volume and price range — so you see how volume built up
 * within each session. `placement` chooses which edge OF THE DAY the bars hug.
 */
export function drawSessionVolumeProfile(params: SessionVolumeProfileDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, yAxis, xAxis, barSpace, indicator } = params
  const cp = indicator.calcParams ?? []
  const rows = numParam(cp, 0, DEFAULT_ROWS)
  const widthPct = numParam(cp, 1, DEFAULT_WIDTH_PCT)
  const valueAreaPct = numParam(cp, 2, DEFAULT_VALUE_AREA_PCT)
  const split = boolParam(cp, 3, true)
  const placeLeft = strParam(cp, 4, DEFAULT_PLACEMENT) === 'left'
  // Where a trading day starts. `prior-after-hours` rolls the boundary back to
  // the previous close, so news-driven after-hours volume profiles WITH the
  // session it moves rather than against the one that already closed.
  const anchor = toSessionAnchor(cp[5])
  const tz = sessionTimezoneOf(indicator.extendData)

  // One memoized profile per (partly) visible session, each grown to its FULL
  // day — see computeSessionProfiles for the growth and invalidation story.
  const spans = computeSessionProfiles(kLineDataList, visibleRange, {
    rows,
    valueAreaPct,
    tz,
    anchor,
  })

  const colors = profileColors(indicator.styles?.lines)

  ctx.save()
  for (const { run, profile } of spans) {
    if (!(maxBucketTotal(profile) > 0)) continue

    // The day's horizontal span, edge to edge of its first/last columns.
    const dayLeftX = xAxis.convertToPixel(run.startIdx) - barSpace.halfBar
    const dayRightX = xAxis.convertToPixel(run.endIdx) + barSpace.halfBar
    const dayWidth = Math.max(dayRightX - dayLeftX, 0)

    paintProfile(ctx, yAxis, {
      profile,
      anchorX: placeLeft ? dayLeftX : dayRightX,
      maxWidth: dayWidth * (widthPct / 100),
      placeLeft,
      pocX0: dayLeftX,
      pocX1: dayRightX,
      split,
      colors,
    })
  }
  ctx.restore()

  return true
}

// ─── Rolling X-Day Volume Profile (VPXD) ─────────────────────────────────────

export interface RollingDaysVolumeProfileDrawParams extends VolumeProfileDrawParams {
  /** Bar-index → x-pixel, for pinning the profile to the START of its own window
   *  rather than to the pane edge. */
  xAxis: { convertToPixel: (dataIndex: number) => number }
  barSpace: { halfBar: number }
  indicator: VolumeProfileDrawParams['indicator'] & { extendData?: unknown }
}

/**
 * Volume Profile (X Days): ONE pane-spanning profile computed from the most
 * recent `days` session days of LOADED data.
 *
 * The point of difference from VPVR is that it profiles a fixed COUNT OF SESSION
 * DAYS rather than whatever bars are on screen — so zooming never redefines how
 * much volume is included. Built for day-2 small caps: the volume that set the
 * levels is the last day or two, and a visible-range profile keeps diluting it.
 *
 * Where those days are measured from is the `windowAnchor` param: pinned to the
 * newest data (never moves), or to the viewport's right edge (scroll a past day
 * into view and you profile that day). See DEFAULT_WINDOW_ANCHOR.
 *
 * `sessionAnchor` decides where a day begins — midnight in the session timezone
 * (`calendar-day`) or the prior day's close (`prior-after-hours`), which keeps a
 * post-close news move attached to the session it drives.
 */
export function drawRollingDaysVolumeProfile(params: RollingDaysVolumeProfileDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, bounding, yAxis, xAxis, barSpace, indicator } = params
  const cp = indicator.calcParams ?? []
  const rows = numParam(cp, 0, DEFAULT_ROWS)
  const widthPct = numParam(cp, 1, DEFAULT_WIDTH_PCT)
  const valueAreaPct = numParam(cp, 2, DEFAULT_VALUE_AREA_PCT)
  const split = boolParam(cp, 3, true)
  const placeLeft = strParam(cp, 4, DEFAULT_PLACEMENT) === 'left'
  const days = Math.max(1, Math.floor(numParam(cp, 5, DEFAULT_DAYS)))
  const anchor = toSessionAnchor(cp[6])
  const windowAnchor =
    strParam(cp, 7, DEFAULT_WINDOW_ANCHOR) === 'visible' ? 'visible' : 'latest-data'
  const tz = sessionTimezoneOf(indicator.extendData)

  const timestamps = timestampsOf(kLineDataList)
  // Where the N days are counted back FROM. `latest-data` ignores the viewport
  // entirely, so the profile never shifts as you pan; `visible` measures from the
  // right edge of the screen, so scrolling a past day into view profiles THAT day
  // instead of today. At the live edge the two are the same window.
  const windowEnd =
    windowAnchor === 'visible'
      ? Math.min(Math.max(visibleRange.to, 0), kLineDataList.length)
      : kLineDataList.length
  const start = lastSessionDaysStart(timestamps, days, tz, anchor, windowEnd)

  // Memoized on the window's [start, windowEnd) — days/anchor/tz enter the key
  // only through the indices they resolve to, which is exactly what the binning
  // sees. Under `latest-data` the window never moves, so every pan/zoom frame
  // is a warm hit; under `visible` it re-bins only when windowEnd crosses a bar.
  const profile = computeRangeProfile(
    kLineDataList,
    start,
    windowEnd,
    { rows, valueAreaPct },
    'vpxd',
  )
  if (!profile || !(maxBucketTotal(profile) > 0)) return true

  /**
   * Pinned to its own WINDOW, not to the pane.
   *
   * The profile describes a specific span of bars, so anchoring it at the pane
   * edge put it wherever you happened to have scrolled — a 2-day profile drawn far
   * left of the two days it actually measured. Anchoring to the window's first bar
   * (or last, when growing leftward) keeps the histogram beside the volume it came
   * from, the way the session profile pins to its own day.
   *
   * CLAMPED to the pane, though. Zoomed inside the window — seeing less than the
   * full N days — its start is off-screen to the left, and an unclamped anchor
   * would put the whole histogram out of view just when you are looking closest at
   * it. So the pin is the window's start OR the pane edge, whichever is further
   * in: it tracks the data while the data is on screen, and parks at the edge
   * otherwise.
   */
  const rawStartX = xAxis.convertToPixel(start) - barSpace.halfBar
  const rawEndX = xAxis.convertToPixel(Math.max(windowEnd - 1, start)) + barSpace.halfBar
  const windowStartX = Math.max(rawStartX, 0)
  const windowEndX = Math.min(rawEndX, bounding.width)

  /**
   * Never wider than the days it describes.
   *
   * `widthPct` is a share of the PANE, so on a zoomed-out chart a 2-day profile
   * could stretch far past the two days it measured and sit over sessions it never
   * looked at. Capping at the distance from the anchor to the window's FAR edge
   * keeps the histogram inside its own span — measured from the clamped anchor, so
   * a window running off-screen is bounded by the part actually visible rather
   * than by its notional width.
   */
  const spanFromAnchor = placeLeft ? rawEndX - windowStartX : windowEndX - rawStartX
  const maxWidth = Math.min(bounding.width * (widthPct / 100), Math.max(spanFromAnchor, 0))

  ctx.save()
  paintProfile(ctx, yAxis, {
    profile,
    anchorX: placeLeft ? windowStartX : windowEndX,
    maxWidth,
    placeLeft,
    // The POC extends from the window forward to the right edge: the level stays
    // relevant after the window ends, but drawing it LEFT of the window would
    // imply it described bars it never saw.
    pocX0: windowStartX,
    pocX1: bounding.width,
    split,
    colors: profileColors(indicator.styles?.lines),
  })
  ctx.restore()

  return true
}

// calc is a no-op carrier for all three profiles — the profiles are
// viewport-dependent, so all real work lives in `draw` (mirrors VOL_OVERLAY).
// One shared frozen element instead of N fresh objects per data update.
const EMPTY_CALC = Object.freeze({})
const emptyCalc = (dataList: KLineData[]) => new Array(dataList.length).fill(EMPTY_CALC)

/** Register the `VPVR` main-pane volume-profile indicator with klinecharts (once). */
export const registerVolumeProfileIndicator = registerOnce(() => {
  registerIndicator({
    name: VPVR_INDICATOR_NAME,
    shortName: 'VPVR',
    // Main (price) pane overlay. 'price' (not 'volume') keeps klinecharts'
    // precision synchronisation from forcing the price axis to integer ticks —
    // same trap VOL_OVERLAY documents.
    series: 'price' as never,
    calcParams: [DEFAULT_ROWS, DEFAULT_WIDTH_PCT, DEFAULT_VALUE_AREA_PCT, true, DEFAULT_PLACEMENT],
    // No figures: the viewport-scoped profile is painted entirely by `draw`, and
    // volume magnitudes must stay out of the price pane's shared y-axis range.
    figures: [],
    // Negative zLevel paints the profile BEHIND the candles.
    zLevel: -1,
    calc: emptyCalc,
    draw: ((p: VolumeProfileDrawParams) => drawVolumeProfile(p)) as never,
    // The legend row carries the "VPVR" label; a canvas tooltip would duplicate
    // it (VOL_OVERLAY precedent).
    createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }),
  } as IndicatorTemplateParam)

  registerIndicator({
    name: VPSV_INDICATOR_NAME,
    shortName: 'Session VP',
    series: 'price' as never,
    calcParams: [
      DEFAULT_ROWS,
      DEFAULT_WIDTH_PCT,
      DEFAULT_VALUE_AREA_PCT,
      true,
      DEFAULT_SESSION_PLACEMENT,
      DEFAULT_SESSION_ANCHOR,
    ],
    figures: [],
    zLevel: -1,
    calc: emptyCalc,
    draw: ((p: SessionVolumeProfileDrawParams & { indicator: Indicator }) =>
      drawSessionVolumeProfile(p)) as never,
    createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }),
  } as IndicatorTemplateParam)

  registerIndicator({
    name: VPXD_INDICATOR_NAME,
    shortName: 'VP Days',
    series: 'price' as never,
    calcParams: [
      DEFAULT_ROWS,
      DEFAULT_WIDTH_PCT,
      DEFAULT_VALUE_AREA_PCT,
      true,
      DEFAULT_PLACEMENT,
      DEFAULT_DAYS,
      DEFAULT_SESSION_ANCHOR,
      DEFAULT_WINDOW_ANCHOR,
    ],
    figures: [],
    zLevel: -1,
    calc: emptyCalc,
    draw: ((p: RollingDaysVolumeProfileDrawParams & { indicator: Indicator }) =>
      drawRollingDaysVolumeProfile(p)) as never,
    createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }),
  } as IndicatorTemplateParam)
})

export { VPVR_INDICATOR_NAME, VPSV_INDICATOR_NAME, VPXD_INDICATOR_NAME }
